import { effect, inject, Injectable, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select } from '@ngxs/store';

import { Permissions } from '@efficiently/models';

import { AppConfigService } from '@efficiently/client-core-services';
import { CoreSelectors } from '@efficiently/client-domain-core';
import { PermissionSelectors } from '@efficiently/client-domain-permissions';
import type { SubscriptionState } from '@efficiently/client-util-classes';

import { SubscriptionRenewalComponent } from '@ds/shared/modules/subscription-renewal/subscription-renewal.component';

import { hasBillingAccess } from './has-billing-access.util';

const RENEWAL_DIALOG_WIDTH = '450px';

/**
 * Opens the subscription renewal dialog each time the subscription state turns to a warning, for a reader who can act
 * on billing.
 *
 * Provide it once per shell, never per menu: the platform shell draws a user menu in the rail and another in the domain
 * navigator's footer, and the prompt must answer the subscription, not the number of menus on screen.
 */
@Injectable()
export class SubscriptionRenewalPromptService {
	private readonly dialog = inject(MatDialog);
	private readonly appConfigService = inject(AppConfigService);
	private readonly subscriptionState = select<SubscriptionState>(CoreSelectors.subscriptionState);
	private readonly globalPermissions = select<Permissions[]>(PermissionSelectors.globalPermissions);

	constructor() {
		// The billing gate is a precondition, not a trigger: tracking it would re-open the dialog on any unrelated
		// permission change while a warning is still standing.
		effect(() => {
			const subscriptionState = this.subscriptionState();

			const canActOnBilling = untracked(() =>
				hasBillingAccess(this.globalPermissions(), this.appConfigService.config.billingEnabled),
			);

			if (!subscriptionState?.isWarning || !canActOnBilling) {
				return;
			}

			this.dialog.open(SubscriptionRenewalComponent, {
				width: RENEWAL_DIALOG_WIDTH,
				data: {
					isLicensed: subscriptionState.isLicensed,
					seconds: subscriptionState.seconds,
					status: subscriptionState.status,
				},
			});
		});
	}
}
